import { Component, Input, OnInit, SimpleChanges } from '@angular/core';
import { AbstractControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-form-formas-pago',
  templateUrl: './form-formas-pago.component.html',
  styleUrls: ['./form-formas-pago.component.scss']
})
export class FormFormasPagoComponent implements OnInit {

  @Input() public form: FormGroup;
  @Input() public totalPagar: number;

  public campoAutoAjustado: string | null = null;

  constructor(
    private translateService: TranslateService
  ) { }

  ngOnInit(): void {
    this.form.setValidators(validarCambio());

    const camposDependientesParaHabilitar = [
      { activador: 'conEfectivo', dependiente: 'importeEfectivo' },
      { activador: 'conEfectivo', dependiente: 'efectivoEntregado' },
      { activador: 'conEfectivo', dependiente: 'cambioDevuelto' },
      { activador: 'conTarjeta', dependiente: 'importeTarjeta' },
      { activador: 'conOtros', dependiente: 'importeOtros' },
    ];

    camposDependientesParaHabilitar.forEach((campos) => {
      this.manageEstadoCampoDependienteDeBooleano(campos.activador, campos.dependiente);
    });

    this.form.get('importeEfectivo')?.valueChanges.subscribe(value => {
      // Si el campo está vacío, ponerlo a 0 para que el validador lo detecte
      if (value === null || value === '' || value === undefined) {
        this.form.get('importeEfectivo')?.setValue(0, { emitEvent: false });
        this.form.get('importeEfectivo')?.markAsTouched();
        value = 0;
      }
      this.form.get('efectivoEntregado').setValue(value);
      this.recalcularImportes('importeEfectivo');
    });
    this.form.get('importeTarjeta')?.valueChanges.subscribe(() => this.recalcularImportes('importeTarjeta'));
    this.form.get('importeOtros')?.valueChanges.subscribe(() => this.recalcularImportes('importeOtros'));
    this.form.get('efectivoEntregado')?.valueChanges.subscribe(() => this.recalcularEfectivoDevuelto());
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['totalPagar']) {
      this.recalcularImportes(null);
    }
  }

  public getTitleCampo(campo: string): string | null {
    return this.campoAutoAjustado === campo
      ? this.translateService.instant('tooltip.formasPago.campoAjustado') // clave en tu archivo de traducciones
      : null;
  }

  isCampoAutoAjustado(campo: string): boolean {
    return this.campoAutoAjustado === campo;
  }

  onInputNumber(event: any, campo: string): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    // Si el valor comienza con 0 y tiene más dígitos, eliminar el 0
    if (value && value.length > 1 && value.startsWith('0') && value[1] !== '.') {
      const numValue = Number(value);
      if (!isNaN(numValue)) {
        this.form.get(campo)?.setValue(numValue, { emitEvent: false });
      }
    }
  }

  // Función para gestionar el estado de los campos dependientes de un booleano
  manageEstadoCampoDependienteDeBooleano(campoPrincipal: string, campodependiente: string) {
    this.form.get(campoPrincipal)?.valueChanges.subscribe((valor) => {
      const dep = this.form.get(campodependiente);
      if (valor) {
        dep?.enable();
        dep?.markAsTouched();
        if (campodependiente !== 'cambioDevuelto') {
          dep?.setValidators([Validators.required, Validators.min(1)]);
        } else {
          dep?.setValidators([Validators.required]);
        }
        dep?.updateValueAndValidity();
        // Recalcular importes cuando se activa un método de pago
        this.recalcularImportes(campodependiente);
      } else {
        dep?.setValue(0);
        dep?.disable();
        dep?.clearValidators();
        // Recalcular importes cuando se desactiva un método de pago
        this.recalcularImportes(null);
      }
    });
  }

  recalcularImportes(campoModificado: string | null) {

    const importeModificado = this.form.get(campoModificado)?.value || 0;
    const restante = this.totalPagar - importeModificado;

    const metodosActivos = [
      this.form.get('conEfectivo')?.value ? 'importeEfectivo' : null,
      this.form.get('conTarjeta')?.value ? 'importeTarjeta' : null,
      this.form.get('conOtros')?.value ? 'importeOtros' : null
    ].filter(Boolean);

    this.rehabilitarCamposActivos(metodosActivos);

    if (metodosActivos.length === 1) {
      const campoUnico = metodosActivos[0];
      // Si solo hay un método activo, asignarle el total
      this.form.patchValue({ [campoUnico]: restante }, { emitEvent: false });
      // Si es efectivo, actualizar el campo específico
      if (campoUnico === 'importeEfectivo') {
        this.form.patchValue({ efectivoEntregado: restante });
      }
      this.desactivarCampoUnico(campoUnico);
    } else if (metodosActivos.length === 2) {
      if (campoModificado != null) {
        if (campoModificado != metodosActivos[0] && campoModificado != metodosActivos[1]) {
          this.manageDobleCampoCambioExterno(metodosActivos, restante);
        } else {
          // Si hay dos activos, ajustar el segundo automáticamente
          const campoAjustar = metodosActivos.find(campo => campo !== campoModificado);
          if (campoAjustar) {
            this.form.patchValue({ [campoAjustar]: restante }, { emitEvent: false });
            // Si es efectivo, actualizar el campo específico
            if (campoAjustar === 'importeEfectivo') {
              this.form.patchValue({ efectivoEntregado: restante });
            }
          }
        }
      } else {
        this.manageDobleCampoCambioExterno(metodosActivos, restante);
      }
    } else if (metodosActivos.length === 3) {
      if (campoModificado == null) {
        // Si hay tres activos, ajustar el primero automáticamente
        this.form.patchValue({ [metodosActivos[0]]: restante }, { emitEvent: false });
        this.form.patchValue({ [metodosActivos[1]]: 0 }, { emitEvent: false });
        this.form.patchValue({ [metodosActivos[2]]: 0 }, { emitEvent: false });
      }
    }
  }

  private desactivarCampoUnico(campo: string) {
    const control = this.form.get(campo);
    control?.disable({ emitEvent: false });
    this.campoAutoAjustado = campo;
  }

  private rehabilitarCamposActivos(campos: string[]) {
    campos.forEach(campo => {
      this.form.get(campo)?.enable({ emitEvent: false });
    });
    this.campoAutoAjustado = null;
  }

  private manageDobleCampoCambioExterno(metodosActivos: string[], restante: number) {
    let importe1 = this.form.get(metodosActivos[0])?.value || 0;
    let importe2 = this.form.get(metodosActivos[1])?.value || 0;
    const sumaActual = importe1 + importe2;
    if (sumaActual != this.totalPagar) {
      this.form.patchValue({ [metodosActivos[0]]: restante }, { emitEvent: false });
      this.form.patchValue({ [metodosActivos[1]]: 0 }, { emitEvent: false });
      metodosActivos.forEach(campo => {
        // Si es efectivo, actualizar el campo específico
        if (campo === 'importeEfectivo') {
          this.form.patchValue({ efectivoEntregado: restante });
        }
      });
    }
  }

  recalcularEfectivoDevuelto(): void {
    const importeEfectivo = this.form.get('importeEfectivo')?.value || 0;
    const efectivoEntregado = this.form.get('efectivoEntregado')?.value || 0;
    this.form.get('cambioDevuelto')?.setValue(efectivoEntregado - importeEfectivo, { emitEvent: false });
  }
}

export function validarCambio(): ValidatorFn {
  return (formGroup: AbstractControl): ValidationErrors | null => {
    const efectivoEntregado = formGroup.get('efectivoEntregado')?.value || 0;
    const cambioDevuelto = formGroup.get('cambioDevuelto')?.value || 0;
    const importeEfectivo = formGroup.get('importeEfectivo')?.value || 0;
    const suma = efectivoEntregado - cambioDevuelto;

    if (suma !== importeEfectivo) {
      return { cambioIncorrecto: true }; // Devuelve un error si no concuerda
    }
    return null; // Sin errores
  };
};
